import { useState } from 'react';

const QUICK_TAGS = [
  { label: 'Fever',           icon: 'bi-thermometer-half' },
  { label: 'Cough & Cold',    icon: 'bi-wind' },
  { label: 'Headache',        icon: 'bi-plus-circle' },
  { label: 'Stomach Pain',    icon: 'bi-emoji-dizzy' },
  { label: 'Skin Concern',    icon: 'bi-droplet' },
  { label: 'Routine Checkup', icon: 'bi-arrow-repeat' },
];

const MAX_CHARS = 500;
const MAX_FILES = 5;
const MAX_FILE_MB = 10;

function isAllowedReport(file) {
  const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
  return allowed.includes(file.type) || /\.(pdf|jpe?g|png)$/i.test(file.name);
}

function AppointmentNotesModal({ show, onClose, onProceed }) {
  const [patientNotes, setPatientNotes] = useState('');
  const [medicalReportFiles, setMedicalReportFiles] = useState([]);

  if (!show) return null;

  const handleProceed = () =>
    onProceed({
      patientNotes,
      medicalReportFiles,
      medicalReportFile: medicalReportFiles[0] || null,
    });
  const handleSkip = () =>
    onProceed({ patientNotes: '', medicalReportFiles: [], medicalReportFile: null });

  const addTag = (label) => {
    setPatientNotes((prev) => {
      const next = prev ? `${prev}, ${label}` : label;
      return next.slice(0, MAX_CHARS);
    });
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setMedicalReportFiles((prev) => {
      const next = [...prev];
      for (const file of incoming) {
        if (next.length >= MAX_FILES) break;
        if (!isAllowedReport(file)) continue;
        if (file.size > MAX_FILE_MB * 1024 * 1024) continue;
        next.push(file);
      }
      return next;
    });
  };

  const removeFile = (index) => {
    setMedicalReportFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadedTime = new Date().toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  /* ─── Shared horizontal padding ─── */
  const hPad = 'clamp(20px, 6vw, 100px)';

  return (
    <div
      tabIndex="-1"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1050,
        backgroundColor: 'rgba(15,23,42,0.65)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',   /* no outer scroll */
      }}
    >
      {/* ── Full-screen card ─────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, background: '#fff', overflow: 'hidden' }}>

        {/* ── Header ──────────────────────────────────────────────── */}
        <div
          className="d-flex align-items-center justify-content-between text-white flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg,#1558e0 0%,#0a47c2 100%)',
            padding: `14px ${hPad}`,
          }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle bg-white bg-opacity-25 flex-shrink-0"
              style={{ width: 42, height: 42 }}
            >
              <i className="bi bi-file-earmark-medical fs-4 text-white"></i>
            </div>
            <div>
              <h5 className="fw-bold mb-0" style={{ fontSize: 17 }}>Health Info & Medical Reports</h5>
              <small className="text-white-50" style={{ fontSize: 12 }}>
                Help your doctor prepare for the best consultation
              </small>
            </div>
          </div>
          <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close" />
        </div>

        {/* ── Body — NO scroll, flex-grows to fill remaining height ── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',   /* vertically center the content */
            padding: `20px ${hPad}`,
            overflow: 'hidden',
            gap: 16,
          }}
        >

          {/* Confidentiality banner */}
          <div
            className="d-flex align-items-center gap-3 rounded-3"
            style={{ background: '#f0f5ff', border: '1px solid #c7d9ff', padding: '10px 16px' }}
          >
            <div
              className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
              style={{ width: 36, height: 36, background: '#1558e0' }}
            >
              <i className="bi bi-shield-check text-white" style={{ fontSize: 16 }}></i>
            </div>
            <div>
              <p className="fw-semibold mb-0 text-dark" style={{ fontSize: 13 }}>
                100% Confidential &amp; Secure
              </p>
              <p className="text-secondary mb-0" style={{ fontSize: 12 }}>
                Your symptoms and reports are only visible to your doctor for this consultation.
              </p>
            </div>
          </div>

          {/* ── Symptoms / Notes ──────────────────────────────────── */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="fw-semibold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: 13.5 }}>
                <i className="bi bi-chat-left-text-fill text-primary"></i>
                Describe Symptoms / Illness Notes
              </label>
              <span className="text-muted" style={{ fontSize: 12 }}>Optional</span>
            </div>

            {/* Quick-add tags */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="text-muted" style={{ fontSize: 12 }}>Quick add:</span>
              {QUICK_TAGS.map(({ label, icon }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => addTag(label)}
                  className="btn btn-sm d-flex align-items-center gap-1 rounded-pill"
                  style={{
                    fontSize: 11.5,
                    padding: '3px 11px',
                    border: '1.5px solid #dee2e6',
                    color: '#495057',
                    background: '#fff',
                    transition: 'all .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = '#1558e0'; e.currentTarget.style.color = '#1558e0'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = '#dee2e6'; e.currentTarget.style.color = '#495057'; }}
                >
                  <i className={`bi ${icon}`} style={{ fontSize: 11.5 }}></i>
                  {label}
                </button>
              ))}
            </div>

            {/* Textarea with counter */}
            <div style={{ position: 'relative' }}>
              <textarea
                className="form-control rounded-3 shadow-none"
                rows="3"
                maxLength={MAX_CHARS}
                placeholder="Describe your current symptoms, how long you've had them, or reason for visit..."
                value={patientNotes}
                onChange={(e) => setPatientNotes(e.target.value)}
                style={{ resize: 'none', fontSize: 13, borderColor: '#dee2e6', paddingBottom: 26 }}
              />
              <span
                className="text-muted"
                style={{ position: 'absolute', bottom: 8, right: 12, fontSize: 11 }}
              >
                {patientNotes.length}/{MAX_CHARS}
              </span>
            </div>
          </div>

          {/* ── Attach Report ─────────────────────────────────────── */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="fw-semibold text-dark mb-0 d-flex align-items-center gap-2" style={{ fontSize: 13.5 }}>
                <i className="bi bi-paperclip text-primary"></i>
                Attach Medical Report / Prescription
              </label>
              <span className="text-muted" style={{ fontSize: 12 }}>
                Optional · up to {MAX_FILES} files
              </span>
            </div>

            {medicalReportFiles.length === 0 ? (
              /* Compact dropzone */
              <label
                className="d-flex align-items-center gap-3 w-100 rounded-3 px-4"
                style={{
                  border: '2px dashed #cbd5e1',
                  cursor: 'pointer',
                  background: '#f8fafc',
                  transition: 'border-color .2s',
                  minHeight: 72,
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = '#1558e0')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#cbd5e1')}
              >
                <input
                  type="file"
                  className="d-none"
                  accept="image/*,.pdf"
                  multiple
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{ width: 40, height: 40, background: '#1558e0' }}
                >
                  <i className="bi bi-cloud-arrow-up-fill text-white" style={{ fontSize: 18 }}></i>
                </div>
                <div>
                  <p className="fw-semibold text-dark mb-0" style={{ fontSize: 13 }}>
                    Click to upload reports or prescriptions
                  </p>
                  <p className="text-muted mb-0" style={{ fontSize: 11.5 }}>
                    PDF, JPG, PNG · Max {MAX_FILE_MB}MB each · Up to {MAX_FILES} files
                  </p>
                </div>
              </label>
            ) : (
              <div>
                <div className="d-flex flex-column gap-2 mb-2" style={{ maxHeight: 160, overflowY: 'auto' }}>
                  {medicalReportFiles.map((file, index) => (
                    <div
                      key={`${file.name}-${index}`}
                      className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2"
                      style={{ border: '1.5px solid #e2e8f0', background: '#fff' }}
                    >
                      <div className="d-flex align-items-center gap-3 overflow-hidden">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                          style={{ width: 42, height: 42, background: '#ffe4e4' }}
                        >
                          <i className="bi bi-file-earmark-pdf-fill text-danger fs-4"></i>
                        </div>
                        <div className="overflow-hidden">
                          <p className="fw-semibold text-dark mb-0 text-truncate" style={{ fontSize: 13 }}>
                            {file.name}
                          </p>
                          <small className="text-muted" style={{ fontSize: 11 }}>
                            {(file.size / (1024 * 1024)).toFixed(1)} MB &bull; Uploaded on {uploadedTime}
                          </small>
                        </div>
                      </div>
                      <div className="d-flex gap-2 ms-3 flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-sm d-flex align-items-center gap-1 rounded-3"
                          style={{ border: '1.5px solid #1558e0', color: '#1558e0', background: '#fff', fontSize: 12, padding: '4px 12px' }}
                          onClick={() => window.open(URL.createObjectURL(file), '_blank')}
                        >
                          <i className="bi bi-eye"></i> View
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm d-flex align-items-center gap-1 rounded-3"
                          style={{ border: '1.5px solid #dc3545', color: '#dc3545', background: '#fff', fontSize: 12, padding: '4px 12px' }}
                          onClick={() => removeFile(index)}
                        >
                          <i className="bi bi-trash3"></i> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                {medicalReportFiles.length < MAX_FILES && (
                  <label
                    className="btn btn-sm btn-outline-primary rounded-3 fw-semibold mb-1"
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="bi bi-plus-lg me-1" />
                    Add more ({medicalReportFiles.length}/{MAX_FILES})
                    <input
                      type="file"
                      className="d-none"
                      accept="image/*,.pdf"
                      multiple
                      onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
                <p className="text-muted mb-0 d-flex align-items-center gap-1" style={{ fontSize: 11.5 }}>
                  <i className="bi bi-info-circle text-primary"></i>
                  Supported formats: PDF, JPG, PNG (Max {MAX_FILE_MB}MB each)
                </p>
              </div>
            )}
          </div>

        </div>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <div
          className="d-flex justify-content-between align-items-center flex-shrink-0"
          style={{ borderTop: '1px solid #e9ecef', background: '#fff', padding: `16px ${hPad}` }}
        >
          <button
            type="button"
            className="btn btn-link text-decoration-none fw-medium"
            style={{ color: '#1558e0', fontSize: 14 }}
            onClick={handleSkip}
          >
            Skip for Now
          </button>
          <button
            type="button"
            className="btn fw-semibold d-flex align-items-center gap-2 px-5 py-2"
            style={{ background: '#1558e0', color: '#fff', borderRadius: 8, fontSize: 14, border: 'none' }}
            onClick={handleProceed}
          >
            Proceed to Payment
            <i className="bi bi-arrow-right"></i>
          </button>
        </div>

      </div>
    </div>
  );
}

export default AppointmentNotesModal;
